import {
  NotificationDisplayText,
  NotificationDto,
  OVERDUE_NOTIFICATION_SUBTYPES,
  resolveNotificationIconBackground,
} from "@/api/dto/notification";
import {NotificationIcons} from "@/components/pages/notifications/NotificationIcon";
import {
  SmallTypography, StandardSemiBoldTypography,
  StandardTypography
} from "patient-ping-remedy/packages/typography";
import React, {useRef, useState, useEffect, useCallback} from "react";
import propsAreEqual from "react-fast-compare";
import DisplayHelpers from "@/helpers/display_helpers";
import Spacer from "patient-ping-remedy/packages/spacer";
import {
  Wrapper,
  LeftWrapper,
  IconWithBackground,
  ContentRow,
  ContentWrapper,
  ViewMoreContainer,
  ViewMoreButton,
  OverdueText
} from "@/components/pages/notifications/NotificationItem.styles";
import {colors} from "patient-ping-remedy/packages/theme";
import WidgetElementViewed from "@common/widgets/WidgetElementViewed";
import {useNotificationItem} from "@/hooks/useNotificationItem";
import NotificationContent from "@/components/pages/notifications/NotificationContent";
import {GroupType, Roster} from "@/api/dto/dto";

type Props = {
  notification: NotificationDto;
  roster?: Roster;
}

function NotificationItem({notification, roster}: Props) {
  const {setNotificationViewed, isApiLoading} = useNotificationItem();
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkTruncation = useCallback(() => {
    const el = contentRef.current;
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
  }, [checkTruncation]);

  const notificationDisplayTextMap = NotificationDisplayText[
    notification.notificationType];
  if (!notificationDisplayTextMap) {
    return null;
  }
  const clientRoster = notification?.client?.roster;

  const notificationDateUTC = new Date(notification.notificationDate);
  const estFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  const notificationDateStringEST = estFormatter.format(notificationDateUTC);
  const todayDateStringEST = estFormatter.format(new Date());

  const isSameDate = notificationDateStringEST === todayDateStringEST;
  const showFormattedDate = (clientRoster?.admittingFacility &&
      !clientRoster.automated &&
      clientRoster.type !== GroupType.CRISIS) && isSameDate;
  const eventAndCategoryText = notificationDisplayTextMap[notification.notificationSubtype]
    ? notificationDisplayTextMap[notification.notificationSubtype](notification)
    : notification.notificationSubtype;
  const timePassed = <SmallTypography>
    {showFormattedDate
      ? DisplayHelpers.formatDate(notification.notificationDate)
      : DisplayHelpers.timeSince(new Date(notification.notificationDate))}
  </SmallTypography>;

  return (
    <Wrapper data-widget-item>
      <LeftWrapper>
        <IconWithBackground
          backgroundColor={resolveNotificationIconBackground(notification.notificationType)}
        >
          {NotificationIcons[notification.notificationType](notification)}
        </IconWithBackground>
        <div>
          <ContentRow>
            <ContentWrapper data-testid={"notification-row"} ref={contentRef} expanded={expanded}>
              <StandardSemiBoldTypography color={colors.primary1}>
                {eventAndCategoryText} <Spacer itemWidth={'5px'}/>
              </StandardSemiBoldTypography>
              <StandardTypography>
                | <Spacer itemWidth={'5px'}/>
                <NotificationContent roster={roster} notification={notification} showSeparator={!isTruncated}/>
                <Spacer itemWidth={'10px'}/>
                {OVERDUE_NOTIFICATION_SUBTYPES.includes(notification.notificationSubtype) && (
                  <><StandardSemiBoldTypography style={{ color: '#B71C1C' }}>
                    <><OverdueText>(Overdue)</OverdueText><Spacer itemWidth={'5px'}/></>
                  </StandardSemiBoldTypography><Spacer itemWidth={'5px'}/></>
                )}
                {!isTruncated && !expanded && timePassed}
              </StandardTypography>
            </ContentWrapper>
            {(isTruncated || expanded) && (
              <ViewMoreContainer collapsed={!expanded}>
                ...
                <ViewMoreButton onClick={() => setExpanded(!expanded)}>
                  {expanded ? 'View less' : 'View more'}
                </ViewMoreButton>
                <Spacer itemWidth={'5px'}/>| <Spacer itemWidth={'5px'}/>
                {timePassed}
              </ViewMoreContainer>
            )}
          </ContentRow>
        </div>
      </LeftWrapper>
      <WidgetElementViewed
        viewed={notification.viewed}
        disabled={isApiLoading}
        onClick={() => setNotificationViewed(notification.id, !notification.viewed)}
        data-testid="mark-notification-as-viewed"
      />
    </Wrapper>
  );
}

export default React.memo(NotificationItem, propsAreEqual);
